import { BillingDocumentType } from "constants/billingDocuments";
import { BUSINESS_ROLE_FILTERS } from "./businesses";

export const SundryTemplateLabel = {
  RCTI_DEPLOYMENT: "RCTI_DEPLOYMENT",
  INVOICE_DEPLOYMENT: "INVOICE_DEPLOYMENT",
  DEPLOYMENT_RCTI: "DEPLOYMENT_RCTI",
  DEPLOYMENT_INVOICE: "DEPLOYMENT_INVOICE",
  RCTI_INVOICE: "RCTI_INVOICE",
  INVOICE_RCTI: "INVOICE_RCTI",
  RCTI_RCTI: "RCTI_RCTI",
  INVOICE_INVOICE: "INVOICE_INVOICE",
};

export const SundryPartyType = {
  DEPLOYMENT: "Deployment",
  INVOICE: "Invoice",
  RCTI: "RCTI",
};

export const deploymentSundryPartyTypeOptions = Object.entries(
  SundryPartyType,
).map(([value, label]) => ({
  value,
  label,
}));

export const saleyardSundryPartyTypeOptions = Object.entries(SundryPartyType)
  .map(([value, label]) => ({
    value,
    label,
  }))
  .filter(value => value.label !== SundryPartyType.DEPLOYMENT);

export const SundryPartyTypeLookup = {
  [SundryTemplateLabel.RCTI_DEPLOYMENT]: {
    fromType: SundryPartyType.RCTI,
    toType: SundryPartyType.DEPLOYMENT,
  },
  [SundryTemplateLabel.INVOICE_DEPLOYMENT]: {
    fromType: SundryPartyType.INVOICE,
    toType: SundryPartyType.DEPLOYMENT,
  },
  [SundryTemplateLabel.DEPLOYMENT_RCTI]: {
    fromType: SundryPartyType.DEPLOYMENT,
    toType: SundryPartyType.RCTI,
  },
  [SundryTemplateLabel.DEPLOYMENT_INVOICE]: {
    fromType: SundryPartyType.DEPLOYMENT,
    toType: SundryPartyType.INVOICE,
  },
  [SundryTemplateLabel.RCTI_INVOICE]: {
    fromType: SundryPartyType.RCTI,
    toType: SundryPartyType.INVOICE,
  },
  [SundryTemplateLabel.INVOICE_RCTI]: {
    fromType: SundryPartyType.INVOICE,
    toType: SundryPartyType.RCTI,
  },
  [SundryTemplateLabel.RCTI_RCTI]: {
    fromType: SundryPartyType.RCTI,
    toType: SundryPartyType.RCTI,
  },
  [SundryTemplateLabel.INVOICE_INVOICE]: {
    fromType: SundryPartyType.INVOICE,
    toType: SundryPartyType.INVOICE,
  },
};

export const MANUAL_TEMPLATE_NAME = "Manual";
export const MANUAL_TEMPLATE_ID = -1;

interface BusinessFieldsReturn {
  toBusinessId?: string;
  fromBusinessId?: string;
}

/**
 * This function builds from and to business references based
 * on if the businesses applied filter field is truthy.
 * eg. filter is buyer and business has isBuyer flagged as true
 * or filter is deployment and business has businessDeploymentId set, etc.
 */
const buildSuggestedBusinessBasedOnFilter = (
  business: Business,
  sundryTemplate: SundryTemplate,
): BusinessFieldsReturn => {
  if (business[BUSINESS_ROLE_FILTERS[sundryTemplate.paidFromSearchFilter]]) {
    return {
      toBusinessId: business.id,
    };
  }
  if (business[BUSINESS_ROLE_FILTERS[sundryTemplate.paidToSearchFilter]]) {
    return {
      fromBusinessId: business.id,
    };
  }
  return {};
};

/**
 * This function returns an object that maps respective business references
 * for a sundry based on the billing document type and sundry template label.
 * used to prefill fields in sundry forms.
 */
export const getSuggestedSundryBusinessFieldsFromBillingDocumentAndTemplate = (
  billingDocumentType: string,
  sundryTemplate: SundryTemplate,
  business: Business,
): BusinessFieldsReturn => {
  if (!business) {
    return {};
  }
  switch (billingDocumentType) {
    case BillingDocumentType.RCTI:
      switch (sundryTemplate.label) {
        case SundryTemplateLabel.RCTI_DEPLOYMENT:
        case SundryTemplateLabel.RCTI_INVOICE:
          return {
            toBusinessId: business.id,
          };
        case SundryTemplateLabel.DEPLOYMENT_RCTI:
        case SundryTemplateLabel.INVOICE_RCTI:
          return {
            fromBusinessId: business.id,
          };
        case SundryTemplateLabel.RCTI_RCTI:
          return buildSuggestedBusinessBasedOnFilter(business, sundryTemplate);
        default:
          return {};
      }
    case BillingDocumentType.INVOICE:
      switch (sundryTemplate.label) {
        case SundryTemplateLabel.INVOICE_DEPLOYMENT:
        case SundryTemplateLabel.INVOICE_RCTI:
          return {
            toBusinessId: business.id,
          };
        case SundryTemplateLabel.DEPLOYMENT_INVOICE:
        case SundryTemplateLabel.RCTI_INVOICE:
          return {
            fromBusinessId: business.id,
          };
        case SundryTemplateLabel.INVOICE_INVOICE:
          return buildSuggestedBusinessBasedOnFilter(business, sundryTemplate);
        default:
          return {};
      }
    default:
      return {};
  }
};

export const getSundryBusinessIdFromDocument = (
  billingDocument: BillingDocument,
): string | null => {
  if (!billingDocument) {
    return null;
  }

  if (billingDocument.type === BillingDocumentType.INVOICE) {
    return billingDocument.issuerBusinessId;
  }
  if (billingDocument.type === BillingDocumentType.RCTI) {
    return billingDocument.recipientBusinessId;
  }
  return null;
};
